import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import Logo from "./Logo";
import WhatsAppLink from "./WhatsAppLink";
import { InstallPwaLink } from "./InstallPwa";
import { useAuth, roleHome } from "../context/AuthContext";
import api from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/servizi", label: "Servizi" },
  { to: "/singola-uscita", label: "Singola uscita" },
  { to: "/abbonamenti", label: "Abbonamenti" },
  { to: "/extra", label: "Extra" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (user?.role !== "professional") return undefined;
    const fetchUnread = () =>
      api.get("/pro/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [user?.role, location.pathname]);

  const areaLabel =
    user?.role === "admin" ? "Area Admin" : user?.role === "professional" ? "Area Professionista" : "Area Cliente";
  const nav = [{ to: user?.role === "customer" ? "/area-cliente" : "/", label: "Home" }, ...NAV.slice(1)];

  return (
    <div className="min-h-screen flex flex-col">
      <header data-testid="main-header" className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-[#e2e8f0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 shrink-0">
            <Logo />
          </Link>
          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-navy bg-[#f0f7ff]" : "text-body hover:text-navy"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Link to={roleHome(user)} data-testid="nav-area" className="btn-secondary !py-2 !px-4 text-sm relative">
                  {areaLabel}
                  {user.role === "professional" && unread > 0 && (
                    <span data-testid="nav-area-badge" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#4a7c59] text-white text-[11px] font-bold flex items-center justify-center shadow-sm">
                      {unread}
                    </span>
                  )}
                </Link>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); window.location.href = "/"; }}
                  className="p-2 rounded-full text-muted-soft hover:text-navy transition-colors duration-200"
                  title="Esci"
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Link to="/accedi" data-testid="nav-login" className="btn-primary !py-2 !px-5 text-sm">
                Accedi
              </Link>
            )}
          </div>
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-navy"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#e2e8f0] bg-white px-4 py-3 space-y-1" data-testid="mobile-menu">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 rounded-xl text-sm font-medium text-navy hover:bg-[#f0f7ff]"
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <NavLink to={roleHome(user)} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-[#4a7c59]">
                  {areaLabel}
                  {user.role === "professional" && unread > 0 && (
                    <span data-testid="nav-area-badge-mobile" className="min-w-5 h-5 px-1 rounded-full bg-[#4a7c59] text-white text-[11px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </NavLink>
                <button
                  data-testid="mobile-logout-btn"
                  onClick={() => { logout(); window.location.href = "/"; }}
                  className="block w-full text-left px-3 py-2.5 rounded-xl text-sm text-muted-soft"
                >
                  Esci
                </button>
              </>
            ) : (
              <NavLink to="/accedi" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-xl text-sm font-semibold text-[#1e3a5f]">
                Accedi
              </NavLink>
            )}
          </div>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <footer data-testid="main-footer" className="bg-[#fefcf9] border-t border-[#ece7dd] mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-8 sm:grid-cols-3">
          <div className="space-y-3">
            <Logo className="h-24" />
            <p className="text-sm text-muted-soft">
              Servizio di cura dei luoghi nei cimiteri, con fotografia finale di ogni intervento.
            </p>
          </div>
          <div className="text-sm text-body space-y-2">
            <p className="eyebrow">Servizio</p>
            <p>Cura del luogo nel cimitero, con fotografia finale dell'intervento.</p>
            <p>Servizio locale, attivo sui cimiteri convenzionati.</p>
          </div>
          <div className="text-sm text-body space-y-2">
            <p className="eyebrow">Contatti</p>
            <p>
              <a href="mailto:Sempreaccanto.servizi@gmail.com" data-testid="contact-email" className="hover:text-navy transition-colors">
                Sempreaccanto.servizi@gmail.com
              </a>
            </p>
            <p>
              <WhatsAppLink testid="contact-whatsapp" />
            </p>
          </div>
        </div>
        <div className="border-t border-[#ece7dd] py-4 text-center text-xs text-muted-soft">
          © {new Date().getFullYear()} Sempre Accanto — Con rispetto, sempre.
          <span className="block mt-1"><InstallPwaLink /></span>
        </div>
      </footer>
    </div>
  );
}
